import React, { useEffect, useState } from 'react'
import useReactRouter from 'use-react-router'
import { Table, Popconfirm, Layout, PageHeader } from 'antd'
import { StripeSettingsDto } from '@merchx-v2/shared-types/dist/dto/stripeSettings/stripe-settings.dto'
import notification from 'mrx-notification'
import { ColumnsType } from 'antd/lib/table'
import useHeightResize from 'utils/useHeightResize'
import { Breadcrumbs } from 'components'
import * as rootStyles from 'assets/layoutStyle'
import styles from './StripeSettingsList.module.scss'
import { connector, PropsFromRedux } from './container'
import CreateStripeSetting from '../CreateStripeSetting'
import UpdateStripeSetting from '../UpdateStripeSetting'

type HeightResizeRule = [number, number, number]

const rules: HeightResizeRule[] = [
  [0, 800, 6],
  [800, 1000, 7],
  [1000, 1200, 9],
  [1200, Infinity, 10]
]

const StripeSettingsList = (props: PropsFromRedux) => {
  const {
    stripeSettings,
    isLoading,
    error,
    isNeedToFetchList,
    total,
    currentPage,
    size,

    changeCurrentPage,
    changePageSize,
    fetchStripeSettingsList,
    removeStripeSetting
  } = props

  const { history } = useReactRouter()
  const [stripeSettingId, setStripeSettingId] = useState<number>()

  const handleChangePageSize = (newSize: number) => {
    changePageSize(newSize)
    fetchStripeSettingsList()
  }

  useHeightResize(handleChangePageSize, rules)

  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Stripe settings list page error!',
        description: error
      })
    }
  }, [error])

  useEffect(() => {
    isNeedToFetchList && fetchStripeSettingsList()
  }, [fetchStripeSettingsList, isNeedToFetchList])

  const doConfirm = async (stripeSettingId: number, e?: React.MouseEvent<HTMLElement>) => {
    e && e.stopPropagation()
    const isRemoved = await removeStripeSetting(stripeSettingId)
    if (isRemoved) {
      notification.success({
        message: 'Successfully',
        description: 'Stripe setting was deleted successfully!'
      })
    }
  }

  const handleOnRowClick = (record: StripeSettingsDto) => ({
    onClick: () => {
      setStripeSettingId(record.id)
    }
  })

  const columns: ColumnsType<StripeSettingsDto> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'API Version',
      dataIndex: 'apiVersion',
      key: 'apiVersion'
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host'
    },
    {
      title: 'Port',
      dataIndex: 'port',
      key: 'port'
    },
    {
      title: 'Secret Key',
      dataIndex: 'secretKey',
      render: (record) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record}</div>,
      key: 'secretKey'
    },
    {
      title: 'Publishable Key',
      dataIndex: 'publishableKey',
      render: (record) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record}</div>,
      key: 'publishableKey'
    },
    {
      title: 'Action',
      key: 'x',
      render: (_value, record) => (
        <span>
          <Popconfirm
            title='Are you sure delete this setting?'
            onConfirm={(e) => doConfirm(record.id, e)}
            onCancel={(e) => e?.stopPropagation()}
            okText='Yes'
            cancelText='No'
          >
            <a
              href='/#'
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              Remove
            </a>
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <Layout className={styles.root}>
      <Layout.Header style={rootStyles.header}>
        <PageHeader onBack={() => history.goBack()} title='Stripe Settings List' extra={[<CreateStripeSetting key='1' />]} />
      </Layout.Header>
      <Layout.Content style={rootStyles.contentBreadcrumbs}>
        <Breadcrumbs
          items={[
            { title: 'Main', url: '/' },
            { title: 'Settings', url: '/' },
            { title: 'Plugins', url: '' },
            { title: 'Stripe Settings', url: '/settings/plugins' }
          ]}
        />
      </Layout.Content>
      <Layout.Content className={styles.contentComponent}>
        <Table
          columns={columns}
          dataSource={stripeSettings}
          bordered
          rowKey={(record) => record.id}
          loading={isLoading}
          onRow={handleOnRowClick}
          pagination={{
            onChange: changeCurrentPage,
            current: currentPage,
            pageSize: size,
            total
          }}
        />
        <UpdateStripeSetting stripeSettingId={stripeSettingId} onClose={() => setStripeSettingId(undefined)} />
      </Layout.Content>
    </Layout>
  )
}

export default connector(StripeSettingsList)
